import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	Input,
	OnInit,
	Output,
} from '@angular/core';
import { openClose } from '@core/animations/animations';
import {
	CondominiumType,
	MatterFieldMappings,
	MatterPropertyType,
	PropertyType,
} from '@models/matter';
import { PropertyUsage } from '@models/title-insurance';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { cloneDeep } from 'lodash';
import { Observable } from 'rxjs';
import { DropdownResult } from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { TestHooks } from './property-type-selection.hooks';
import {
	condoTypes,
	propertyTypesResidential,
	propertyTypesCommercial,
	PropertyTypeOptions,
} from './property-type-selection.lookups';
import { AI_GENERATED_ICON } from '@core/util/icon';

@UntilDestroy()
@Component({
	selector: 'app-property-type-selection',
	templateUrl: './property-type-selection.component.html',
	animations: [openClose()],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class PropertyTypeSelectionComponent implements OnInit {
	testHooks = TestHooks;
	public propertyTypeItems: PropertyTypeOptions[] = [];
	public displayAsDropdown: boolean;
	public selectedPropertyTypeItem: PropertyTypeOptions = null;
	public AI_ICON = AI_GENERATED_ICON.icon;
	public AI_TOOLTIP = AI_GENERATED_ICON.tooltip;
	public AI_CLASSES = AI_GENERATED_ICON.classes;

	public propertyTypes = PropertyType;
	public condoTypes = condoTypes;

	@Input() set propertyType(incomingPropertyType: MatterPropertyType) {
		this._propertyType = cloneDeep(incomingPropertyType);
	}

	@Output() onPropertyTypeChange = new EventEmitter<MatterPropertyType>();

	public _propertyType: MatterPropertyType;

	@SelectSnapshot(SelectedMatter.id) matterId: string;
	@Select(SelectedMatter.propertyType)
	propertyType$: Observable<MatterPropertyType>;
	constructor(private matterDispatch: MatterDispatch) {}
	@Select(SelectedMatter.getFieldMappings)
	fieldMappings$: Observable<MatterFieldMappings>;

	ngOnInit(): void {
		this.propertyType$.pipe(untilDestroyed(this)).subscribe((propertyType) => {
			this.propertyTypeItems = (propertyType?.usage ===
			PropertyUsage.RESIDENTIAL
				? propertyTypesResidential
				: propertyTypesCommercial) as unknown as PropertyTypeOptions[];
			this.displayAsDropdown = this.propertyTypeItems.length > 5;
			this.selectedPropertyTypeItem = this.propertyTypeItems.find((type) => {
				return type.value === this._propertyType?.type;
			});
		});
	}

	propertyTypeUpdated($event: PropertyType) {
		this._propertyType.type = $event;

		this.updatePropertyType();
	}

	propertyTypeUpdatedDropdown($event: DropdownResult<PropertyTypeOptions>) {
		this._propertyType.type = $event.result.value;
		this.selectedPropertyTypeItem = $event.result;

		this.updatePropertyType();
	}

	propertyOtherUpdated($event: string) {
		this._propertyType.otherType = $event;
		const updated = this._propertyType;
		this.onPropertyTypeChange.emit(updated);
	}

	condoTypeUpdated($event: CondominiumType) {
		this._propertyType.condoType = $event;
		const updated = this._propertyType;
		this.onPropertyTypeChange.emit(updated);
	}

	private updatePropertyType() {
		if (
			this._propertyType.type === PropertyType.CONDO &&
			!this._propertyType.condoType
		) {
			this._propertyType.condoType = CondominiumType.STANDARD;
		}

		const updated = this._propertyType;
		this.onPropertyTypeChange.emit(updated);
	}
}
